/** @file NodeSection: the selected node's fields (pos, rest, IK, flex parent, hidden, group, offset). */
import type { PuppetNode } from '@zilo/pose3d-format/types/puppet';
import type { ReactElement } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { NumericField } from '../../ui-kit/numericInput/NumericField';
import { NumericRow } from '../../ui-kit/numericInput/NumericRow';
import { InspectorSection } from './InspectorSection';
import { VecInput } from './VecInput';

/** The fields `node.set` may change. */
type NodeFields = Partial<Omit<PuppetNode, 'parent' | 'mirror'>>;

const GROUPS = [1, 2, 3, 4, 5, 6, 7] as const;
const row = { display: 'flex', alignItems: 'center', gap: 6 };
const label = { width: 92, color: 'var(--muted, #8b919e)' };
const ZERO: [number, number, number] = [0, 0, 0];

/** The two checkboxes and the group select. */
function Flags({ node, set }: { node: PuppetNode; set: (fields: NodeFields) => void }): ReactElement {
  return (
    <>
      <label style={row}><span style={label}>flex parent</span>
        <input type="checkbox" checked={node.flexParent === true} onChange={(e) => { set({ flexParent: e.currentTarget.checked }); }} /></label>
      <label style={row}><span style={label}>hidden</span>
        <input type="checkbox" checked={node.hidden === true} onChange={(e) => { set({ hidden: e.currentTarget.checked }); }} /></label>
      <label style={row}><span style={label}>group</span>
        <select aria-label="group" value={node.group ?? ''}
          onChange={(e) => { const g = GROUPS.find((n) => String(n) === e.currentTarget.value); if (g !== undefined) set({ group: g }); }}>
          <option value="" disabled>none</option>
          {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select></label>
    </>
  );
}

/** Every edit is one `node.set { name, fields }` batch; `parent` and `mirror` live in the tree (reparent, mirror pair). */
export function NodeSection({ name }: { name: string }): ReactElement | null {
  const store = useEditorStore();
  const node = useEditor((s) => s.puppet.doc?.puppet.nodes[name]);
  if (node === undefined) return null;
  const set = (fields: NodeFields): void => {
    void store.getState().dispatch([{ name: 'node.set', input: { name, fields } }], `set ${name} ${Object.keys(fields).join(', ')}`);
  };
  return (
    <InspectorSection title="Node" aside={node.parent === undefined ? 'root' : `parent ${node.parent}`}>
      <VecInput label="pos" unit="unit" value={node.pos} onCommit={(pos) => { set({ pos }); }} step={0.01} />
      <VecInput label="rest" unit="deg" value={node.rest ?? ZERO} onCommit={(rest) => { set({ rest }); }} step={1} />
      <VecInput label="origin offset" unit="unit" value={node.originOffset ?? ZERO} onCommit={(originOffset) => { set({ originOffset }); }} step={0.01} />
      <NumericRow label="IK chain">
        <NumericField label="ik" value={node.ik ?? 0} step={1} onCommit={(ik) => { set({ ik: Math.max(0, Math.round(ik)) }); }} />
      </NumericRow>
      <Flags node={node} set={set} />
    </InspectorSection>
  );
}
